import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "inline-flex items-center justify-center rounded-control border px-2 py-0.5 text-meta font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1.5 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring focus-visible:ring-2 aria-invalid:ring-destructive aria-invalid:border-destructive transition-[color,box-shadow] duration-[var(--motion-fast)] ease-[var(--ease-standard)] overflow-hidden",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary-hover",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-muted",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground [a&]:hover:bg-destructive-hover focus-visible:ring-destructive",
        outline:
          "text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        // Academic state wears the status roles, never the tenant accent. A
        // positive standing painted `default` renders in --primary, so the same
        // state reads lapis at Murtada and harbor at Mufid, and the strongest
        // colour on the page - reserved for the one primary action - is spent
        // on every row of a table. These pairs are contrast-tested at 4.5:1 in
        // both presets by token-governance.test.ts.
        success:
          "border-transparent bg-status-success-surface text-status-success-foreground",
        warning:
          "border-transparent bg-status-warning-surface text-status-warning-foreground",
        info:
          "border-transparent bg-status-info-surface text-status-info-foreground",
      },
      dot: {
        true: "before:size-1.5 before:shrink-0 before:rounded-full before:bg-current before:content-['']",
        false: "",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

function Badge({
  className,
  variant,
  dot = variant === "success" || variant === "warning" || variant === "info" || variant === "destructive",
  asChild = false,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : "span";

  return (
    <Comp
      data-slot="badge"
      data-dot={dot || undefined}
      className={cn(badgeVariants({ variant, dot }), className)}
      {...props}
    />
  );
}

export { Badge, badgeVariants };
